import { Download } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { getPublicationStatus, getQueryRut } from 'lib/api/documentPublication';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Disclaimer from 'lib/designSystem/Disclaimer';
import { DocumentPublication } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ClientPublicationForm from './components/ClientPublicationForm';
import InvoiceForm from './components/InvoiceForm';
import SuccessDialog from './components/SuccessDialog';

interface PublicationFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  documentId?: string;
}

type PublicationType = 'invoice' | 'client';

export type FormProps = {
  documentId?: string;
  setLoading: (open: boolean) => void;
  setOpen: (open: boolean) => void;
  handleSuccessClose: () => void;
  setIdentifier: (identifier: string) => void;
};

const formRegistry: Record<string, React.FC<FormProps>> = {
  invoice: InvoiceForm,
  client: ClientPublicationForm,
};

const PublicationForm: React.FC<PublicationFormProps> = (props) => {
  const { open, setOpen, documentId } = props;
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const [type, setType] = useState<PublicationType | ''>('');

  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState<string>('');

  const dispatch = useDispatch();

  const [canPublishInvoice, setCanPublishInvoice] = useState(true);
  const [canPublishClient, setCanPublishClient] = useState(true);

  const [invoiceCertificateLink, setInvoiceCertificateLink] = useState('');
  const [clientCertificateLink, setClientCertificateLink] = useState('');

  const handleDownloadCertificate = (certificateLink: string) => {
    window.open(certificateLink, '_blank');
  };

  const handleTypeChange = (e: SelectChangeEvent<string>) => {
    setType(e.target.value as PublicationType);
  };

  const handleSuccessClose = () => {
    setOpen(false);
    setSuccessDialogOpen(true);
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    setIdentifier('');
    setOpen(false);
  };

  const fetchPublicationStatus = async () => {
    if (!documentId) return;
    const publications = await getPublicationStatus(dispatch, documentId);
    const hasInvoicePublication = publications.some(
      (p: DocumentPublication) =>
        p.type == 'Factura' && p.unpublishedAt == null,
    );
    setCanPublishInvoice(!hasInvoicePublication);
    const hasClientPublication = publications.some(
      (p: DocumentPublication) =>
        (p.type == 'Contrato' && p.unpublishedAt == null) ||
        (p.type == 'Pagare Adeudado' && p.unpublishedAt == null),
    );
    setCanPublishClient(!hasClientPublication);

    if (!document) return alert('Ha ocurrido un error');

    if (hasInvoicePublication) {
      const { creditorIdentifier: stakeholderIdentifier } = publications.find(
        (p) => p.type == 'Factura',
      )!;

      const { certificateLink } = await getQueryRut(
        dispatch,
        stakeholderIdentifier,
      );
      setInvoiceCertificateLink(certificateLink);
    }

    if (hasClientPublication) {
      const { creditorIdentifier: businessIdentifier } = publications.find(
        (p) => p.type == 'Contrato' || p.type == 'Pagare Adeudado',
      )!;
      const { certificateLink } = await getQueryRut(
        dispatch,
        businessIdentifier,
      );
      setClientCertificateLink(certificateLink);
    }
  };

  useEffect(() => {
    setType('');
    if (!documentId) return;
    try {
      void fetchPublicationStatus();
    } catch (error) {
      console.error(error);
    }
  }, [documentId]);

  const FormComponent = type ? formRegistry[type] : null;

  if (loading) {
    return <CenteredCircularProgress />;
  }

  return (
    <div>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4"> Publicar</Typography>
            </Grid>
            <Grid item>
              <img
                src="https://pruff-public.s3.amazonaws.com/Integrations/cobranzaOnline.svg"
                alt="cobranzaOnlineLogo"
              />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} overflow={'hidden'}>
            <Grid item xs={12}>
              <InputLabel id="filter-label">
                Selecciona el tipo de documento
              </InputLabel>

              <Select fullWidth value={type} onChange={handleTypeChange}>
                <MenuItem value="invoice" disabled={!canPublishInvoice}>
                  Deudor (Publicar factura)
                </MenuItem>
                <MenuItem value="client" disabled={!canPublishClient}>
                  Cliente (Publicar contrato impago y/o pagaré)
                </MenuItem>
              </Select>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                Ingresa la información del documento que deseas publicar.
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ my: 2 }} />
          {FormComponent && (
            <FormComponent
              documentId={documentId}
              setLoading={setLoading}
              setOpen={setOpen}
              handleSuccessClose={handleSuccessClose}
              setIdentifier={setIdentifier}
            />
          )}
          {!FormComponent && (
            <div>
              {!canPublishInvoice && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Disclaimer text="Esta factura ya fue publicada (Deudor publicado)." />
                  <IconButton
                    color="primary"
                    onClick={() =>
                      handleDownloadCertificate(invoiceCertificateLink)
                    }
                  >
                    <Download />
                  </IconButton>
                </Box>
              )}
              {!canPublishClient && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Disclaimer text="Este documento tiene una publicación por contrato impago (Emisor publicado)." />
                  <IconButton
                    color="primary"
                    onClick={() =>
                      handleDownloadCertificate(clientCertificateLink)
                    }
                  >
                    <Download />
                  </IconButton>
                </Box>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <SuccessDialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        identifier={identifier}
      />
    </div>
  );
};

export default PublicationForm;
